import { Link } from "react-router-dom";
import { brl } from "@/lib/api";
import { Button } from "@/components/ui-kit";
import { Printer, FileText, Wallet, PauseCircle } from "lucide-react";

/**
 * Totais e ações de fechamento — a mesma decisão, em dois formatos.
 *
 * Desktop: rodapé `sticky` DENTRO do scroll da coluna de fechamento. Antes era
 * um bloco fixo que crescia com o conteúdo, e numa tela mais baixa ele comia
 * quase toda a altura da coluna: o campo de busca de cliente virava uma fatia de
 * poucos pixels (era o "encolhe as informações do cliente").
 *
 * Celular: barra colada no rodapé da página, porque lá o total e o botão ficam a
 * uma tela inteira de distância do carrinho.
 */

/** Rodapé do fechamento (desktop). */
export function SaleTotals({
  subtotal, discount, shipping = 0, total, cashOpen, cashRegisterPath,
  onFinalize, onHold, onQuote, canFinalize, hasItems, busy, lastSale, onPrint, onEmitNfe,
}) {
  return (
    <div className="hidden lg:block sticky bottom-0 bg-white border-t border-slate-200 p-4 space-y-3">
      <div className="text-sm text-slate-500 flex justify-between">
        <span>Subtotal</span><span className="font-mono tabular-nums">{brl(subtotal)}</span>
      </div>
      {discount > 0 && (
        <div className="text-sm text-red-500 flex justify-between">
          <span>Desconto</span><span className="font-mono tabular-nums">-{brl(discount)}</span>
        </div>
      )}
      {/* Frete somando, em contraste com o desconto que abate logo acima: a
          cor e o sinal são o que separa os dois de relance. */}
      {shipping > 0 && (
        <div className="text-sm text-slate-600 flex justify-between">
          <span>Frete</span><span className="font-mono tabular-nums" data-testid="pos-shipping-line">+{brl(shipping)}</span>
        </div>
      )}
      {/* O total é o número que o cliente confere na tela: maior elemento
          tipográfico do PDV, em mono para os dígitos não dançarem. */}
      <div className="flex items-end justify-between gap-2 pt-1">
        <span className="text-eyebrow uppercase tracking-wider text-slate-500 pb-1.5">Total</span>
        <span className="font-mono tabular-nums text-2xl xl:text-display-sm font-semibold text-slate-900 leading-none"
          data-testid="pos-total">{brl(total)}</span>
      </div>

      {cashOpen === false ? (
        /* Com o caixa fechado o botão vira secundário e aponta para a ação que
           destrava, em vez de só repetir o estado. */
        <Link to={cashRegisterPath} className="block" data-testid="pos-open-cash-link">
          <Button variant="secondary" size="xl" block icon={Wallet}>Abrir o caixa para vender</Button>
        </Link>
      ) : (
        <Button onClick={onFinalize} disabled={!canFinalize} loading={busy} size="xl" block
          data-testid="pos-finalize-btn">
          {busy ? "Processando…" : "Finalizar venda"}
        </Button>
      )}

      {/* Guardar e Orçamento dividem a linha: são os dois destinos alternativos
          do carrinho, e nenhum deve competir com o botão de fechar a venda. */}
      <div className="grid grid-cols-2 gap-2">
        <Button onClick={onHold} disabled={!hasItems || busy} variant="secondary" icon={PauseCircle}
          data-testid="pos-hold-btn">Guardar</Button>
        <Button onClick={onQuote} disabled={!hasItems || busy} variant="outline" icon={FileText}
          data-testid="pos-quote-btn">Orçamento</Button>
      </div>

      {lastSale && (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-500 pt-1"
          data-testid="pos-last-sale">
          <span>
            Última: <span className="font-mono">{lastSale.number}</span> · {brl(lastSale.total)}
            {lastSale.invoice && <> · NFC-e <span className="font-mono">{lastSale.invoice.number}</span></>}
          </span>
          <Button variant="link" size="sm" icon={Printer} onClick={onPrint} data-testid="pos-print-receipt">Cupom</Button>
          <Button variant="link" size="sm" onClick={onEmitNfe} disabled={busy} data-testid="pos-emit-nfe">Emitir NF-e</Button>
        </div>
      )}
    </div>
  );
}

/**
 * Barra de ação do celular. `sticky` (não `fixed`) para respeitar o scroll do painel.
 *
 * Guardar e Orçamento moram AQUI também, e não só no rodapé do desktop: aquele
 * rodapé é `hidden lg:block`, então no celular os dois destinos alternativos do
 * carrinho simplesmente não existiam — quem atendia pelo tablet não tinha como
 * guardar a venda nem gerar orçamento, as duas coisas que mais acontecem quando
 * o cliente diz "vou pensar".
 */
export function MobileActionBar({
  total, discount, cashOpen, cashRegisterPath, canFinalize, busy, onFinalize, onGoToPayment,
  onHold, onQuote, hasItems,
}) {
  return (
    <div className="lg:hidden sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-4px_16px_-8px_rgba(15,23,42,.25)]"
      data-testid="pos-mobile-bar">
      {/* Linha de cima, discreta: são saídas da venda, não o fechamento dela —
          e não podem disputar o polegar com "Finalizar". */}
      {hasItems && (
        <div className="grid grid-cols-2 gap-2 mb-2.5">
          <Button onClick={onHold} disabled={busy} variant="secondary" size="sm" icon={PauseCircle}
            data-testid="pos-hold-btn-mobile">Guardar</Button>
          <Button onClick={onQuote} disabled={busy} variant="outline" size="sm" icon={FileText}
            data-testid="pos-quote-btn-mobile">Orçamento</Button>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 leading-none">Total</p>
          <p className="font-mono tabular-nums text-xl font-semibold text-slate-900 leading-tight"
            data-testid="pos-total-mobile">{brl(total)}</p>
          {discount > 0 && <p className="text-[11px] text-red-500 leading-none">-{brl(discount)} de desconto</p>}
        </div>
        <div className="flex-1" />
        {cashOpen === false ? (
          <Link to={cashRegisterPath}><Button variant="secondary" size="lg" icon={Wallet}>Abrir caixa</Button></Link>
        ) : canFinalize ? (
          <Button onClick={onFinalize} loading={busy} size="lg" data-testid="pos-finalize-btn-mobile">Finalizar</Button>
        ) : (
          /* Sem pagamento fechado o botão LEVA ao pagamento em vez de falhar. Um
             "Finalizar" que só emite um toast de erro é o que fazia o vendedor
             tocar três vezes achando que a tela tinha travado. */
          <Button variant="secondary" size="lg" onClick={onGoToPayment} data-testid="pos-goto-payment">
            Pagamento
          </Button>
        )}
      </div>
    </div>
  );
}
